import { useEffect, useRef } from 'react';

// Запас до срока: видео, запущенное перед обновлением ссылок, успевает доиграть
const REFRESH_MARGIN_MS = 30 * 60 * 1000;
// Если по часам браузера срок уже прошёл, не перезапрашиваем сразу после ответа
const MIN_REFRESH_DELAY_MS = 10 * 1000;
// Запрос не принёс новых ссылок (нет сети) — повторяем не чаще этого
const RETRY_DELAY_MS = 5 * 60 * 1000;
// Таймер спящего устройства опаздывает, поэтому срок ещё и сверяется с часами раз в минуту
const CHECK_INTERVAL_MS = 60 * 1000;

const getEarliestTime = (dates: Array<string | null | undefined>): number | null => {
  const times = dates
    .map((date) => (date ? Date.parse(date) : Number.NaN))
    .filter((time) => Number.isFinite(time));
  return times.length > 0 ? Math.min(...times) : null;
};

/**
 * Ссылки на видео Cloudflare Stream подписаны и живут до `urlExpiresAt`. Бэкенд строит их на
 * каждый ответ, поэтому до этого срока ответ с видео перезапрашивается, а ссылки не хранятся.
 */
export const useRefreshOnVideoUrlExpiry = (
  urlExpiresAt: Array<string | null | undefined>,
  refetch: () => unknown,
) => {
  const earliestExpiry = getEarliestTime(urlExpiresAt);
  const refetchRef = useRef(refetch);

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (earliestExpiry === null) {
      return undefined;
    }

    const receivedAt = Date.now();
    // Короткие ссылки обновляются на середине оставшегося срока
    const margin = Math.min(REFRESH_MARGIN_MS, Math.max(earliestExpiry - receivedAt, 0) / 2);
    const refreshAt = Math.max(earliestExpiry - margin, receivedAt + MIN_REFRESH_DELAY_MS);
    let lastRefreshAt: number | null = null;

    // Новые ссылки меняют срок и перезапускают эффект; пока срок прежний, запрос повторяется
    const checkExpiry = () => {
      const now = Date.now();
      if (now < refreshAt || (lastRefreshAt !== null && now - lastRefreshAt < RETRY_DELAY_MS)) {
        return;
      }
      lastRefreshAt = now;
      refetchRef.current();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkExpiry();
      }
    };

    const timeoutId = window.setTimeout(checkExpiry, refreshAt - receivedAt);
    const intervalId = window.setInterval(checkExpiry, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [earliestExpiry]);
};
